import {Avatar, Skeleton, Stack, Typography} from "@mui/material"
import {useQuery, UseQueryResult} from "@tanstack/react-query"
import {FC, Fragment, useMemo, useState} from "react"
import {decodeToken} from "react-jwt"
import {Pencil} from "../../../assets"
import {useConfigProvider} from "../../../config"
import {stringToAvatar} from "../../../helpers"
import {accountEndpoints} from "../../../services"
import {Contact, Token} from "../../../types"
import {NewButton} from "../../atoms"
import {TextWithTooltip} from "../../molecules"
import {Trans, useTranslation} from "react-i18next"
import ProfileInfoForm from "./ProfileInfoForm"
import {SupportModal} from "../.."
import {langs} from "../../../pages/Profile/Preferences/constants"
import {useExpiringLocalStorage} from "../../../hooks/useExpiringLocalStorage"
import {CrmAccount} from "../../../types/CrmAccount"

interface ProfileInfoProps {
  getProfileInfoQuery: UseQueryResult<Contact, Error>
  getInternalUserQuery?: UseQueryResult<Contact, Error>
}
const ProfileInfo: FC<ProfileInfoProps> = ({
  getProfileInfoQuery,
  getInternalUserQuery,
}) => {
  const {token} = useConfigProvider()
  const {t} = useTranslation()
  const {getProfilePicture, getJobFunctions} = accountEndpoints()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const decoded = decodeToken<Token>(token)
  const accountNumber = decoded?.companyId || ""
  const email = decoded?.email
  const [getTempLanguage] = useExpiringLocalStorage<string>(
    "tempLanguage",
    "",
    10 * 60 * 1000,
  )

  const [showSupportModal, setShowSupportModal] = useState<boolean>(false)

  const getProfilePictureQuery = useQuery({
    queryKey: ["profilePicture", email],
    queryFn: () => getProfilePicture(decoded?.email!!, "120x120"),
    enabled: !!email && !email.includes("@controlrisks.com"),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const getJobFunctionsQuery = useQuery({
    queryKey: ["jobFunctions"],
    queryFn: () => getJobFunctions(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const jobFunction = useMemo(() => {
    if (
      !getJobFunctionsQuery.data ||
      !getProfileInfoQuery.data ||
      !getProfileInfoQuery.data.jobFunction
    ) {
      return ""
    }

    const found = getJobFunctionsQuery.data.find(
      (func) => func.id === getProfileInfoQuery.data.jobFunction,
    )

    return found?.jobFunction || ""
  }, [getJobFunctionsQuery.data, getProfileInfoQuery.data])

  const profilePicture = useMemo(() => {
    if (getProfilePictureQuery.data) {
      return URL.createObjectURL(getProfilePictureQuery.data)
    }
    return ""
  }, [getProfilePictureQuery.data])

  const firstName =
    getProfileInfoQuery.data?.firstName || getInternalUserQuery?.data?.firstName
  const lastName =
    getProfileInfoQuery.data?.lastName || getInternalUserQuery?.data?.lastName

  const fullName = `${firstName || ""} ${lastName || ""}`

  const hasProfileInfoError =
    getProfileInfoQuery.isError ||
    getProfileInfoQuery.isLoadingError ||
    getProfileInfoQuery.isRefetchError ||
    !getProfileInfoQuery.data

  const hasInternalUserError =
    getInternalUserQuery &&
    (getInternalUserQuery.isError ||
      getInternalUserQuery.isLoadingError ||
      getInternalUserQuery.isRefetchError ||
      !getInternalUserQuery.data)

  const isError = hasProfileInfoError && hasInternalUserError

  const isLoading =
    getProfileInfoQuery.isLoading ||
    getProfileInfoQuery.isFetching ||
    getInternalUserQuery?.isLoading ||
    getInternalUserQuery?.isFetching ||
    isError

  const handleToggleEdit = () => {
    setIsEditing((prev) => !prev)
  }

  const handleOpenSupportModal = () => {
    setShowSupportModal((open) => !open)
  }

  const handleRefetch = () => {
    if (getInternalUserQuery) {
      getInternalUserQuery.refetch()
    }
    getProfileInfoQuery.refetch()
    getProfilePictureQuery.refetch()
    getJobFunctionsQuery.refetch()
  }

  return (
    <Stack
      component="aside"
      direction="column"
      width="236px"
      minWidth="236px"
      height="100%"
      bgcolor="#ffffff"
      paddingX="20px"
      paddingY="24px"
      gap="24px"
      boxShadow="4px 4px 10px 0 #59595833"
      position="fixed"
    >
      {isEditing ? (
        <ProfileInfoForm
          profileInfo={getProfileInfoQuery.data!}
          handleToggleEdit={handleToggleEdit}
        />
      ) : (
        <Fragment>
          {isError && !isLoading ? (
            <Stack
              height="100%"
              width="100%"
              direction="column"
              position="absolute"
              bgcolor="#FFFFFFB3"
              top="0"
              left="0"
              zIndex="10"
              gap="20px"
              paddingTop="159px"
              paddingX="30px"
              alignItems="center"
            >
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="1.43"
                textAlign="center"
              >
                <Trans
                  i18nKey="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or contact us."
                  defaults="We're sorry, we're having trouble retrieving this right now. Please try refreshing your browser or <ContactLink>contact us</ContactLink>"
                  components={{
                    ContactLink: (
                      <Typography
                        component="span"
                        color="#226CA5"
                        fontSize="0.875rem"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={handleOpenSupportModal}
                      >
                        contact us
                      </Typography>
                    ),
                  }}
                />
              </Typography>
              <NewButton
                variant="outline"
                color="primary"
                text={t("Refresh")}
                onClick={handleRefetch}
                sx={{
                  height: "32px",
                }}
              />
            </Stack>
          ) : null}
          <Stack
            component="header"
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            width="100%"
          >
            <Typography
              fontSize="20px"
              fontWeight="600"
              lineHeight="1.2"
              color="#003d50"
            >
              {isLoading ? (
                <Fragment>
                  <Skeleton variant="text" width="164px" animation="wave" />
                  <Skeleton variant="text" width="164px" animation="wave" />
                </Fragment>
              ) : (
                <Fragment>
                  {getProfileInfoQuery.data?.firstName ||
                    getInternalUserQuery?.data?.firstName}
                  <br />
                  {getProfileInfoQuery.data?.lastName ||
                    getInternalUserQuery?.data?.lastName}
                </Fragment>
              )}
            </Typography>
            {accountNumber !== CrmAccount.Internal &&
              (isLoading ? (
                <Skeleton variant="rounded" width="24px" height="24px" />
              ) : (
                <NewButton
                  size="small"
                  icon
                  variant="text"
                  color="secondary"
                  disabled={isLoading || !getProfileInfoQuery.data}
                  IconLeft={<Pencil />}
                  onClick={handleToggleEdit}
                  sx={{
                    minWidth: "0",
                    width: "24px",
                    height: "24px",
                    padding: "0",
                  }}
                />
              ))}
          </Stack>
          <Stack
            component="article"
            direction="row"
            alignItems="center"
            justifyContent="center"
            width="100%"
            aria-label="Profile picture"
          >
            {isLoading ||
            getProfilePictureQuery.isLoading ||
            getProfilePictureQuery.isFetching ? (
              <Skeleton
                variant="circular"
                animation="wave"
                width="103px"
                height="103px"
              />
            ) : (
              <Avatar
                {...stringToAvatar(fullName, {
                  width: "103px",
                  height: "103px",
                  fontSize: "40px",
                  fontWeight: "600",
                  lineHeight: "1.4",
                })}
                src={profilePicture || undefined}
                alt={profilePicture ? fullName : undefined}
                slotProps={
                  profilePicture
                    ? {
                        img: {width: "103px", height: "103px", loading: "lazy"},
                      }
                    : undefined
                }
              />
            )}
          </Stack>
          <Stack component="section" direction="column" gap="16px" width="100%">
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="jobTitle"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Job title")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={getProfileInfoQuery.data?.jobTitle || ""}
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="jobFunction"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ||
                getJobFunctionsQuery.isLoading ||
                getJobFunctionsQuery.isFetching ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Job function")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ||
                getJobFunctionsQuery.isLoading ||
                getJobFunctionsQuery.isFetching ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={
                      jobFunction ||
                      getProfileInfoQuery.data?.jobFunction ||
                      getInternalUserQuery?.data?.jobFunction ||
                      ""
                    }
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="emailAddress"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Email address")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={
                      getProfileInfoQuery.data?.emailAddress ||
                      getInternalUserQuery?.data?.emailAddress ||
                      ""
                    }
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="accountName"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Account name")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={
                      accountNumber === CrmAccount.Internal
                        ? "Control Risks"
                        : getProfileInfoQuery.data?.accountName || ""
                    }
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="accountName"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Business phone number")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={
                      getProfileInfoQuery.data?.businessPhone ||
                      getInternalUserQuery?.data?.businessPhone ||
                      ""
                    }
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipProps={{
                      arrow: true,
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
            <Stack
              component="article"
              direction="column"
              gap="5px"
              id="accountName"
            >
              <Typography
                fontSize="16px"
                fontWeight="400"
                color="#224957"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="98px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <Fragment>{t("Preferred language")}</Fragment>
                )}
              </Typography>
              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#595958"
                lineHeight="normal"
              >
                {isLoading ? (
                  <Fragment>
                    <Skeleton width="164px" variant="text" animation="wave" />
                  </Fragment>
                ) : (
                  <TextWithTooltip
                    text={(() => {
                      const tempLang = getTempLanguage()
                      const preferredLang =
                        getProfileInfoQuery.data?.preferredLanguage?.toString()

                      const selectedLang =
                        langs.find((lang) => lang.code === tempLang) ||
                        langs.find((lang) => lang.value === preferredLang)

                      return selectedLang?.label || ""
                    })()}
                    limit={22}
                    includeEllipsis={false}
                    textStyle={{
                      fontSize: "14px",
                      fontWeight: "400",
                      color: "#595958",
                      lineHeight: "normal",
                    }}
                    tooltipProps={{
                      arrow: true,
                    }}
                    tooltipStyle={{
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#FFFFFF",
                        height: "26px",
                        maxWidth: "100vw",
                        color: "#242D35",
                        textAlign: "left",
                        fontSize: "14px",
                        lineHeight: "22px",
                        fontWeight: "400",
                        paddingX: "8px",
                        paddingY: "2px",
                        borderWidth: "0.5px",
                        borderStyle: "solid",
                        borderColor: "#1B1B1B",
                        borderRadius: "0px",
                      },
                    }}
                  />
                )}
              </Typography>
            </Stack>
          </Stack>
        </Fragment>
      )}

      <SupportModal
        show={showSupportModal}
        handleToggle={handleOpenSupportModal}
        title="New client support request"
      />
    </Stack>
  )
}

ProfileInfo.displayName = "ProfileInfo"
export default ProfileInfo
